import { useEffect, useState, useMemo } from "react";
import { COLOR, FONT, RADIUS, SPACE } from "../lib/tokens";
import { supabase } from "../lib/supabase";

// StepAssignmentBanner — replaces the Timer/Start UI when the
// current step is actively owned by ANOTHER participant. Renders
// the assignment status with appropriate copy + the lead's
// management actions (Revoke / Take Over).
//
// Status states the banner can be in:
//   PENDING       — "Assigned to Marissa. Waiting on her acceptance…"
//   DECLINED      — "Marissa declined. Reassign or take over."
//   ACCEPTED      — "Marissa accepted! Waiting for her to start the timer…"
//   IN_PROGRESS   — "Marissa is on it." + live ticking countdown
//                   reading her timer_state.endsAt
//   PAUSED        — "Marissa paused at 2:30. Waiting for her to resume…"
//   DONE          — "Marissa finished!" (transient — usually the
//                   assignment row gets archived next)
//
// Lead actions:
//   Revoke — calls unassignStep, frees the step for re-assignment
//            or for the lead to claim themselves.
//   Take Over — calls takeOverStep (lead-only force-reassign).
//
// Props:
//   assignment   — the active cook_step_assignments row
//   assigneeName — string, hydrated upstream from profiles
//   isLead       — bool; gates Revoke/Take Over visibility
//   stepId       — step id for the action callbacks
//   liveStepRow  — the open cook_session_steps row for this step
//                  (may be null if assignee hasn't started). When
//                  present, drives the IN_PROGRESS / PAUSED state.
//   onRevoke     — async fn called for the Revoke action
//   onTakeOver   — async fn called for the Take Over action

// Modes:
//   "other"           — step is assigned to someone else; show their
//                       status, lead gets Revoke + Take Over buttons,
//                       non-lead is read-only
//   "mine-pending"    — step assigned to me, awaiting my acceptance;
//                       show "You've been assigned" + Accept / Decline
//   "viewer"          — step has no assignment AND I'm not the lead.
//                       Static "Waiting on the lead" until SOMEONE
//                       starts the step (lead or any helper) — at
//                       which point this banner upgrades to a live
//                       ticking countdown showing whose progress this
//                       is. So a viewer always sees what's happening,
//                       just without controls.
export default function StepAssignmentBanner({
  mode = "other",
  assignment,
  assigneeName,
  isLead,
  stepId,
  cookSessionId,
  onRevoke,
  onTakeOver,
  onAccept,
  onDecline,
}) {
  // Pull the open step row for this step + assignee so we can
  // surface her timer state inside the banner. In "other" mode we
  // scope to the assignee's user_id (only her work counts). In
  // "viewer" mode there's no assignee — we want ANY open row for
  // this step, so a lead-driven start surfaces a live countdown.
  // Subscribed for realtime so paused/resumed/started transitions
  // reflect instantly.
  const [stepRow, setStepRow] = useState(null);
  const [stepRowOwnerName, setStepRowOwnerName] = useState("");
  useEffect(() => {
    if (!cookSessionId || !stepId) return undefined;
    if (mode !== "viewer" && !assignment?.assigned_to) return undefined;
    let alive = true;
    const baseId = String(stepId);
    const userFilter = mode === "viewer" ? null : assignment?.assigned_to;
    const fetch = async () => {
      // Filter the query to rows belonging to THIS step (or its
      // stages — `<base>-s<n>`). Without this filter, .limit(1)
      // could return the most-recent row for ANY step in the session;
      // when that row didn't match baseId we'd setStepRow(null) (idle)
      // OR — worse — the most-recent row for THIS step that we
      // actually wanted could lose to a NEWER row for a different
      // step. The viewer banner kept "unloading" the live timer when
      // the lead inserted a fresh row on a different step.
      let q = supabase
        .from("cook_session_steps")
        .select("id, step_id, started_at, finished_at, nominal_seconds, timer_state, user_id")
        .eq("cook_session_id", cookSessionId)
        .or(`step_id.eq.${baseId},step_id.like.${baseId}-s*`)
        .is("finished_at", null)
        .order("started_at", { ascending: false })
        .limit(1);
      if (userFilter) q = q.eq("user_id", userFilter);
      const { data } = await q.maybeSingle();
      if (!alive) return;
      console.log("[StepBanner:fetch]",
        "mode=", mode,
        "baseId=", baseId,
        "userFilter=", userFilter,
        "rowStepId=", data?.step_id,
        "rowUserId=", data?.user_id,
        "timer_state=", JSON.stringify(data?.timer_state),
        "endsAtMs=", data?.timer_state?.endsAt
          ? new Date(data.timer_state.endsAt).getTime()
          : null,
        "nowMs=", Date.now());
      if (data && String(data.step_id).replace(/-s\d+$/, "") === baseId) {
        setStepRow(data);
        // In viewer mode, hydrate the cook's first name once we
        // know who's actually cooking it. Cached in component state
        // so the countdown rerender doesn't re-fetch every tick.
        if (mode === "viewer" && data.user_id) {
          const { data: prof } = await supabase
            .from("profiles")
            .select("name")
            .eq("id", data.user_id)
            .maybeSingle();
          if (alive) {
            const full = prof?.name || "";
            setStepRowOwnerName(full ? full.split(/\s+/)[0] : "Cook");
          }
        }
      } else {
        setStepRow(null);
        setStepRowOwnerName("");
      }
    };
    fetch();
    const channelKey = mode === "viewer"
      ? `rt:viewer-step:${cookSessionId}:${baseId}`
      : `rt:assignment-step:${assignment.id}`;
    const ch = supabase
      .channel(channelKey)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cook_session_steps",
          filter: `cook_session_id=eq.${cookSessionId}` },
        (payload) => {
          if (!alive) return;
          const row = payload.new || payload.old;
          if (!row) return;
          // viewer mode: any user. other mode: only the assignee.
          if (userFilter && row.user_id !== userFilter) return;
          // Either mode: only react to rows for THIS step.
          if (String(row.step_id).replace(/-s\d+$/, "") !== baseId) return;
          fetch();
        },
      )
      .subscribe();
    return () => { alive = false; try { supabase.removeChannel(ch); } catch {} };
  }, [cookSessionId, stepId, assignment?.id, assignment?.assigned_to, mode]);

  // Tick for live countdown when assignee is actively running her
  // timer. No-op for other phases.
  const [, setTick] = useState(0);
  const ts = stepRow?.timer_state || null;
  const ticking = ts?.endsAt && !ts.isPaused && !ts.ended;
  useEffect(() => {
    if (!ticking) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [ticking]);

  const phase = useMemo(() => {
    if (assignment?.status === "declined") return "declined";
    if (assignment?.status === "pending") return "pending";
    if (assignment?.status === "done") return "done";
    // Status is accepted or in_progress — look at timer_state to
    // decide whether she's actually running or just sitting on it.
    if (!ts) return "accepted";
    if (ts.ended) return "ended";
    if (ts.isPaused) return "paused";
    if (!ts.endsAt) return "accepted";
    const remaining = Math.round((new Date(ts.endsAt).getTime() - Date.now()) / 1000);
    if (remaining <= 0) return "ended";
    return "running";
  }, [assignment?.status, ts]);

  const fmt = (s) => {
    if (s == null) return "—";
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
  };

  // displaySec for the live ticker in the running/paused cases.
  let displaySec = null;
  if (phase === "running" && ts?.endsAt) {
    displaySec = Math.max(0, Math.round((new Date(ts.endsAt).getTime() - Date.now()) / 1000));
  } else if (phase === "paused") {
    displaySec = Math.max(0, Number(ts?.pausedRemainingSec) || 0);
  } else if (phase === "ended") {
    displaySec = 0;
  }

  const headline = (() => {
    switch (phase) {
      case "pending":   return `Assigned to ${assigneeName}`;
      case "declined":  return `${assigneeName} declined`;
      case "accepted":  return `${assigneeName} accepted!`;
      case "running":   return `${assigneeName} is cooking`;
      case "paused":    return `${assigneeName} paused`;
      case "ended":     return `${assigneeName}'s timer rang`;
      case "done":      return `${assigneeName} finished`;
      default:          return assigneeName;
    }
  })();

  const subline = (() => {
    switch (phase) {
      case "pending":   return "Waiting on her acceptance…";
      case "declined":  return "Reassign or take over.";
      case "accepted":  return "Waiting for her to tap Start…";
      case "running":   return "Step in progress.";
      case "paused":    return "Waiting for her to resume.";
      case "ended":     return "Timer's up — waiting on her to mark done.";
      case "done":      return "";
      default:          return "";
    }
  })();

  const accent = (() => {
    switch (phase) {
      case "pending":  return COLOR.sub;
      case "declined": return COLOR.rose || "#C44F3A";
      case "accepted": return COLOR.gold;
      case "running":  return COLOR.leaf;
      case "paused":   return "#C28F2C";
      case "ended":    return COLOR.leaf;
      case "done":     return COLOR.leaf;
      default:         return COLOR.sub;
    }
  })();

  // ── MODE: mine-pending — I've been assigned, awaiting my decision
  if (mode === "mine-pending") {
    return (
      <div style={{
        position: "relative",
        borderRadius: 18,
        padding: `${SPACE.section}px ${SPACE.section}px`,
        background: COLOR.deep,
        border: `2px dashed ${COLOR.gold || "#D4A637"}`,
        marginTop: SPACE.md,
        marginBottom: SPACE.md,
      }}>
        <div style={{
          fontFamily: FONT.mono,
          fontSize: 10,
          letterSpacing: 1.4,
          color: COLOR.gold || "#D4A637",
          fontWeight: 700,
          marginBottom: 8,
        }}>
          YOUR ASSIGNMENT
        </div>
        <div style={{
          fontFamily: FONT.serif || FONT.detail,
          fontStyle: "italic",
          fontSize: 24,
          color: COLOR.ink,
          lineHeight: 1.2,
          marginBottom: 8,
        }}>
          You've been assigned this step
        </div>
        <div style={{
          fontFamily: FONT.detail,
          fontSize: 14,
          fontStyle: "italic",
          color: COLOR.muted,
          lineHeight: 1.5,
          marginBottom: SPACE.md,
        }}>
          Accept to claim this step and unlock the timer, or decline to send
          it back to the lead.
        </div>
        <div style={{
          display: "flex",
          gap: 8,
        }}>
          <button
            type="button"
            className="mcm-focusable"
            onClick={() => onDecline?.()}
            style={{
              flex: 1,
              padding: "12px 14px",
              background: "transparent",
              border: `1px solid ${COLOR.edge}`,
              borderRadius: 10,
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.8,
              color: COLOR.sub,
              cursor: "pointer",
            }}
          >
            DECLINE
          </button>
          <button
            type="button"
            className="mcm-focusable"
            onClick={() => onAccept?.()}
            style={{
              flex: 2,
              padding: "12px 14px",
              background: COLOR.leaf || "#2F8F83",
              border: "none",
              borderRadius: 10,
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: 0.8,
              color: "#FFFFFF",
              cursor: "pointer",
            }}
          >
            ACCEPT
          </button>
        </div>
      </div>
    );
  }

  // ── MODE: viewer — unassigned step viewed by non-lead participant.
  //   • No active row yet → static "Waiting on the lead" message.
  //   • Active row exists → live ticking countdown showing whose
  //     progress this is, derived from the row's timer_state.
  if (mode === "viewer") {
    // Re-use the same phase / displaySec / fmt logic the "other"
    // branch uses, but anchored to stepRow + stepRowOwnerName instead
    // of the assignment + assigneeName.
    const viewerPhase = (() => {
      if (!stepRow) return "idle";
      if (!ts) return "ready";
      if (ts.ended) return "ended";
      if (ts.isPaused) return "paused";
      if (!ts.endsAt) return "ready";
      const remaining = Math.round((new Date(ts.endsAt).getTime() - Date.now()) / 1000);
      if (remaining <= 0) return "ended";
      return "running";
    })();
    let viewerDisplaySec = null;
    if (viewerPhase === "running" && ts?.endsAt) {
      viewerDisplaySec = Math.max(0, Math.round((new Date(ts.endsAt).getTime() - Date.now()) / 1000));
    } else if (viewerPhase === "paused") {
      viewerDisplaySec = Math.max(0, Number(ts?.pausedRemainingSec) || 0);
    } else if (viewerPhase === "ended") {
      viewerDisplaySec = 0;
    }
    const viewerName = stepRowOwnerName || "Someone";
    const viewerHeadline = (() => {
      switch (viewerPhase) {
        case "running": return `${viewerName} is cooking`;
        case "paused":  return `${viewerName} paused`;
        case "ended":   return `${viewerName}'s timer rang`;
        case "ready":   return `${viewerName} is on it`;
        default:        return null;
      }
    })();
    const viewerSubline = (() => {
      switch (viewerPhase) {
        case "running": return "Step in progress.";
        case "paused":  return "Waiting for them to resume.";
        case "ended":   return "Timer's up — waiting on them to mark done.";
        case "ready":   return "Waiting for them to tap Start…";
        default:        return null;
      }
    })();
    const viewerAccent = (() => {
      switch (viewerPhase) {
        case "running": return COLOR.leaf;
        case "paused":  return "#C28F2C";
        case "ended":   return COLOR.leaf;
        case "ready":   return COLOR.gold;
        default:        return COLOR.sub;
      }
    })();

    if (viewerPhase === "idle") {
      return (
        <div style={{
          position: "relative",
          borderRadius: 18,
          padding: `${SPACE.md}px ${SPACE.section}px`,
          background: COLOR.deep,
          border: `1px solid ${COLOR.edge}`,
          marginTop: SPACE.md,
          marginBottom: SPACE.md,
        }}>
          <div style={{
            fontFamily: FONT.mono,
            fontSize: 10,
            letterSpacing: 1.4,
            color: COLOR.sub,
            fontWeight: 700,
            marginBottom: 6,
          }}>
            WATCHING
          </div>
          <div style={{
            fontFamily: FONT.detail,
            fontSize: 14,
            fontStyle: "italic",
            color: COLOR.muted,
            lineHeight: 1.4,
          }}>
            This step doesn't have a cook yet. Waiting on the lead to start
            or assign it.
          </div>
        </div>
      );
    }

    return (
      <div style={{
        position: "relative",
        borderRadius: 18,
        padding: `${SPACE.lg}px ${SPACE.section}px`,
        background: COLOR.deep,
        border: `1.5px dashed ${viewerAccent}`,
        marginTop: SPACE.md,
        marginBottom: SPACE.md,
      }}>
        <div style={{
          fontFamily: FONT.mono,
          fontSize: 10,
          letterSpacing: 1.4,
          color: viewerAccent,
          fontWeight: 700,
          marginBottom: 6,
        }}>
          WATCHING · {viewerPhase.toUpperCase()}
        </div>
        <div style={{
          display: "flex",
          alignItems: viewerDisplaySec != null ? "baseline" : "center",
          justifyContent: "space-between",
          gap: SPACE.md,
          marginBottom: viewerSubline ? 8 : 0,
        }}>
          <div style={{
            fontFamily: FONT.serif || FONT.detail,
            fontStyle: "italic",
            fontSize: 22,
            color: COLOR.ink,
            lineHeight: 1.2,
          }}>
            {viewerHeadline}
          </div>
          {viewerDisplaySec != null && (
            <div style={{
              fontFamily: FONT.mono,
              fontSize: 30,
              fontWeight: 800,
              fontVariantNumeric: "tabular-nums",
              color: viewerAccent,
              letterSpacing: "0.02em",
              lineHeight: 1,
            }}>
              {fmt(viewerDisplaySec)}
            </div>
          )}
        </div>
        {viewerSubline && (
          <div style={{
            fontFamily: FONT.detail,
            fontSize: 14,
            fontStyle: "italic",
            color: COLOR.muted,
          }}>
            {viewerSubline}
          </div>
        )}
      </div>
    );
  }

  // ── MODE: other — step assigned to someone else (default)
  return (
    <div style={{
      position: "relative",
      borderRadius: 18,
      padding: `${SPACE.lg}px ${SPACE.section}px`,
      background: COLOR.deep,
      border: `1.5px dashed ${accent}`,
      marginTop: SPACE.md,
      marginBottom: SPACE.md,
    }}>
      <div style={{
        fontFamily: FONT.mono,
        fontSize: 10,
        letterSpacing: 1.4,
        color: accent,
        fontWeight: 700,
        marginBottom: 6,
      }}>
        STEP LOCKED · {phase.toUpperCase().replace("_", " ")}
      </div>

      {/* Viewer countdown — same model as Paul's kids-mode card.
          The owner sees the FULL Timer (Start/Pause/Reset buttons);
          non-owners see the same live ticking digits but with no
          interactive controls. Information without interaction. */}
      <div style={{
        display: "flex",
        alignItems: displaySec != null ? "baseline" : "center",
        justifyContent: "space-between",
        gap: SPACE.md,
        marginBottom: subline ? 8 : 0,
      }}>
        <div style={{
          fontFamily: FONT.serif || FONT.detail,
          fontStyle: "italic",
          fontSize: 22,
          color: COLOR.ink,
          lineHeight: 1.2,
        }}>
          {headline}
        </div>
        {displaySec != null && (
          <div style={{
            fontFamily: FONT.mono,
            fontSize: 30,
            fontWeight: 800,
            fontVariantNumeric: "tabular-nums",
            color: accent,
            letterSpacing: "0.02em",
            lineHeight: 1,
          }}>
            {fmt(displaySec)}
          </div>
        )}
      </div>

      {subline && (
        <div style={{
          fontFamily: FONT.detail,
          fontSize: 14,
          fontStyle: "italic",
          color: COLOR.muted,
          marginBottom: isLead ? SPACE.md : 0,
        }}>
          {subline}
        </div>
      )}

      {/* Lead actions. Helpers + diners get no buttons here — they
          just see the read-only status. */}
      {isLead && (
        <div style={{
          display: "flex",
          gap: 8,
          marginTop: SPACE.sm,
        }}>
          <button
            type="button"
            className="mcm-focusable"
            onClick={() => onRevoke?.()}
            style={{
              flex: 1,
              padding: "10px 14px",
              background: "transparent",
              border: `1px solid ${COLOR.edge}`,
              borderRadius: 10,
              fontFamily: FONT.mono,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.8,
              color: COLOR.sub,
              cursor: "pointer",
            }}
          >
            REVOKE
          </button>
          <button
            type="button"
            className="mcm-focusable"
            onClick={() => onTakeOver?.()}
            style={{
              flex: 1,
              padding: "10px 14px",
              background: COLOR.gold || "#C28F2C",
              border: `1px solid ${COLOR.goldDim || "#A47013"}`,
              borderRadius: 10,
              fontFamily: FONT.mono,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.8,
              color: "#1E1E1E",
              cursor: "pointer",
            }}
          >
            TAKE OVER
          </button>
        </div>
      )}
    </div>
  );
}
